import { Fragment, useMemo } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useTheme } from "styled-components";

import { Legend } from "components/Chart";

export const LineChart = ({
  data = [],
  lines = [],
  legendContent,
  cartesianGridProps,
  margin = {
    top: 5,
    right: 0,
    left: 0,
    bottom: 5,
  },
  tooltipContent,
  yAxisProps,
  xAxisProps,
  lineProps,
  strokeWidth = 2,
  lineChartProps,
  tooltipProps,
  referenceLineProps,
  showAreaFill = false,
}) => {
  const theme = useTheme();

  // Generate a unique ID for this chart instance to avoid gradient conflicts
  const chartId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  return (
    <ResponsiveContainer>
      <ComposedChart data={data} margin={margin} {...lineChartProps}>
        {cartesianGridProps && (
          <CartesianGrid vertical={false} {...cartesianGridProps} />
        )}

        {xAxisProps && xAxisProps !== false && <XAxis {...xAxisProps} />}

        {yAxisProps && yAxisProps !== false && (
          <YAxis
            {...yAxisProps}
            tick={false}
            axisLine={false}
            tickLine={false}
          />
        )}

        {tooltipContent && (
          <Tooltip content={tooltipContent} {...tooltipProps} />
        )}

        {legendContent && (
          <Legend
            layout="horizontal"
            verticalAlign="top"
            align="center"
            iconSize={10}
            height={50}
            {...legendContent}
          />
        )}

        {lines.map((line, i) => {
          return (
            <Fragment key={i}>
              {(line.segments || showAreaFill) && (
                <defs>
                  {line.segments && (
                    <linearGradient id={`gradient-${chartId}-${i}`}>
                      {line.segments.map((seg, j) => {
                        return (
                          <stop
                            key={j}
                            offset={`${seg.percentage}%`}
                            stopColor={`${seg.color}`}
                          />
                        );
                      })}
                    </linearGradient>
                  )}
                  {showAreaFill && (
                    <linearGradient
                      id={`areaGradient-${chartId}-${i}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={line.color}
                        stopOpacity={0.2}
                      />
                      <stop
                        offset="100%"
                        stopColor={line.color}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  )}
                </defs>
              )}

              {showAreaFill ? (
                <Area
                  type="monotone"
                  dataKey={line.key}
                  stroke={
                    lineProps?.gradient ? `url(#gradient-${i})` : line.color
                  }
                  strokeWidth={strokeWidth}
                  fill={`url(#areaGradient-${chartId}-${i})`}
                  className="line-chart-legend"
                  {...lineProps}
                />
              ) : (
                <Line
                  dot={false}
                  type="monotone"
                  dataKey={line.key}
                  key={`data-${i}-line`}
                  strokeWidth={strokeWidth}
                  className="line-chart-legend"
                  stroke={
                    lineProps?.gradient ? `url(#gradient-${i})` : line.color
                  }
                  {...lineProps}
                />
              )}
            </Fragment>
          );
        })}
        {referenceLineProps && (
          <ReferenceLine
            stroke={theme.chart.line.color}
            strokeDasharray="6 6"
            {...referenceLineProps}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

LineChart.propTypes = {
  data: PropTypes.array,
  cartesianGridProps: PropTypes.object,
  lineProps: PropTypes.object,
  lines: PropTypes.array,
  legendContent: PropTypes.object,
  margin: PropTypes.object,
  tooltipContent: PropTypes.elementType,
  yAxisProps: PropTypes.object,
  xAxisProps: PropTypes.object,
  lineChartProps: PropTypes.object,
  strokeWidth: PropTypes.number,
  tooltipProps: PropTypes.object,
  referenceLineProps: PropTypes.object,
  showAreaFill: PropTypes.bool,
};
